import { useEffect, useRef, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { locale, t } from "../i18n";
import { navigate } from "../router";
import type { FileKind, PostedFile } from "../sync/types";
import { inlineImages } from "./Attachments";
import { IconSearch, IconTrash } from "./icons";
import { Card, EmptyState, Page } from "./ui";

// kinds are the kinds of file one can pick, with what they are called.
const kinds = (): [FileKind, string][] => [
  ["image", t("Images")],
  ["pdf", t("PDFs")],
  ["document", t("Documents")],
  ["spreadsheet", t("Spreadsheets")],
  ["presentation", t("Presentations")],
  ["media", t("Audio and video")],
  ["archive", t("Archives")],
  ["code", t("Code and text")],
  ["other", t("Other files")],
];

// kindNames name one file of each kind.
const kindNames = (): Record<FileKind, string> => ({
  image: t("Image"),
  pdf: "PDF",
  document: t("Document"),
  spreadsheet: t("Spreadsheet"),
  presentation: t("Presentation"),
  media: t("Audio or video"),
  archive: t("Archive"),
  code: t("Code or text"),
  other: t("File"),
});

// tiles are the letters on a file's tile, by its kind.
const tiles: Record<FileKind, string> = { image: "IMG", pdf: "PDF", document: "DOC", spreadsheet: "XLS", presentation: "PPT", media: "▶", archive: "ZIP", code: "</>", other: "FILE" };

function size(n: number) {
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Tile is a file's picture: the image itself, small, or its kind.
function Tile({ f }: { f: PostedFile }) {
  const session = useSession();
  const [url, setURL] = useState<string | null>(null);
  const image = inlineImages.has(f.file.mime);
  useEffect(() => {
    if (!image) return;
    let live = true;
    session.fileURL(f.file.id).then((u) => live && setURL(u), () => {});
    return () => {
      live = false;
    };
  }, [f.file.id, image, session]);
  return (
    <span className={`file-tile ${f.kind}`} aria-hidden>
      {url ? <img src={url} alt="" loading="lazy" /> : tiles[f.kind]}
    </span>
  );
}

// FilesView is every file posted where one can read it, as Slack's Files:
// newest first, found by name, kind, who posted it and where.
export function FilesView() {
  const session = useSession();
  const state = useLantern();
  const [name, setName] = useState("");
  const [typed, setTyped] = useState("");
  const [kind, setKind] = useState<FileKind | "">("");
  const [from, setFrom] = useState("");
  const [channel, setChannel] = useState("");
  const [files, setFiles] = useState<PostedFile[] | null>(null);
  const [next, setNext] = useState("");
  const [error, setError] = useState("");
  const [loadingOlder, setLoadingOlder] = useState(false);
  // asked counts the filters' changes, so a page asked for under earlier
  // filters is dropped when it comes.
  const asked = useRef(0);

  // The name is looked for once typing pauses.
  useEffect(() => {
    const timer = setTimeout(() => setName(typed.trim()), 250);
    return () => clearTimeout(timer);
  }, [typed]);
  const query = { name, kind, from, channel };
  useEffect(() => {
    const n = ++asked.current;
    setError("");
    setLoadingOlder(false);
    session.api.files(query).then(
      (r) => n === asked.current && (setFiles(r.files), setNext(r.next_cursor)),
      (err) => n === asked.current && setError(err instanceof Error ? err.message : String(err)),
    );
  }, [session, name, kind, from, channel]);
  const older = async () => {
    if (loadingOlder || !next) return;
    const n = asked.current;
    setLoadingOlder(true);
    try {
      const r = await session.api.files({ ...query, cursor: next });
      if (n !== asked.current) return;
      setFiles((shown) => [...(shown ?? []), ...r.files]);
      setNext(r.next_cursor);
    } catch (err) {
      if (n === asked.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (n === asked.current) setLoadingOlder(false);
    }
  };
  // remove deletes a file for everyone, from every message it is in.
  async function remove(f: PostedFile) {
    if (!window.confirm(t("Delete {name}? It goes from every message it is in, for everyone, and cannot be undone.", { name: f.file.name }))) return;
    setError("");
    try {
      await session.api.deleteFile(f.file.id);
      setFiles((shown) => (shown ?? []).filter((x) => x.file.id !== f.file.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  async function open(f: PostedFile) {
    try {
      window.open(await session.fileURL(f.file.id), "_blank", "noopener");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const me = state.me?.id;
  const admin = state.me?.role === "admin";
  const people = [...session.directory.values()]
    .filter((p) => p.id !== me && p.status === "active" && p.kind !== "system")
    .sort((a, b) => (a.display_name || a.handle).localeCompare(b.display_name || b.handle, locale));
  const places = [...state.channels.values()]
    .map((c) => c.channel)
    .filter((c) => !c.archived_at)
    .sort((a, b) => session.channelName(a.id).localeCompare(session.channelName(b.id), locale));
  const kindName = kindNames();
  const filtered = !!(name || kind || from || channel);

  return (
    <Page className="files-view" title={t("Files")} description={t("Everything shared in your channels and conversations, newest first.")}>
      <div className="search-bar" role="search">
        <span className="search-icon" aria-hidden>
          <IconSearch size={18} />
        </span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t("Search file names")} aria-label={t("Search file names")} />
      </div>
      <div className="search-filters" role="group" aria-label={t("Filters")}>
        <select className={`chip ${kind ? "on" : ""}`} value={kind} onChange={(e) => setKind(e.target.value as FileKind | "")} aria-label={t("Type")}>
          <option value="">{t("All types")}</option>
          {kinds().map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        <select className={`chip ${from ? "on" : ""}`} value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t("From")}>
          <option value="">{t("From anyone")}</option>
          {me && <option value={me}>{t("From you")}</option>}
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {t("From {name}", { name: p.display_name || p.handle })}
            </option>
          ))}
        </select>
        <select className={`chip ${channel ? "on" : ""}`} value={channel} onChange={(e) => setChannel(e.target.value)} aria-label={t("In")}>
          <option value="">{t("Anywhere")}</option>
          {places.map((c) => (
            <option key={c.id} value={c.id}>
              {t("In {place}", { place: session.channelName(c.id) })}
            </option>
          ))}
        </select>
        {filtered && (
          <button
            className="chip"
            onClick={() => {
              setTyped("");
              setName("");
              setKind("");
              setFrom("");
              setChannel("");
            }}
          >
            {t("Clear filters")}
          </button>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      <Card>
        {files === null ? (
          <p className="muted">{t("Loading…")}</p>
        ) : files.length === 0 && !next ? (
          filtered ? (
            <EmptyState icon="🔍" title={t("No files match")}>
              {t("Try other words, or fewer filters.")}
            </EmptyState>
          ) : (
            <EmptyState icon="📁" title={t("No files yet")}>
              {t("Files posted in your channels and conversations, in messages or threads, are listed here.")}
            </EmptyState>
          )
        ) : (
          <>
            <ul className="rows files-all">
              {files.map((f) => (
                <li key={f.file.id + f.message_id}>
                  <Tile f={f} />
                  <span className="row-main">
                    <button className="row-title link" onClick={() => void open(f)} title={t("Open {name}", { name: f.file.name })}>
                      {f.file.name}
                    </button>
                    <span className="row-meta">
                      {[
                        kindName[f.kind],
                        size(f.file.size),
                        session.name(f.author_id),
                        session.channelName(f.channel_id),
                        new Date(f.posted_at).toLocaleDateString(locale, { month: "short", day: "numeric", year: new Date(f.posted_at).getFullYear() === new Date().getFullYear() ? undefined : "numeric" }),
                      ].join(" · ")}
                    </span>
                  </span>
                  <button
                    className="small ghost"
                    onClick={() => navigate(f.thread_root_id ? `/c/${f.channel_id}/t/${f.thread_root_id}` : `/c/${f.channel_id}/m/${f.message_id}`)}
                  >
                    {t("Show message")}
                  </button>
                  {(f.author_id === me || admin) && (
                    <button className="small ghost" onClick={() => void remove(f)} aria-label={t("Delete {name}", { name: f.file.name })} title={t("Delete")}>
                      <IconTrash size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {next && (
              <div className="files-more">
                <button onClick={() => void older()} disabled={loadingOlder}>
                  {loadingOlder ? t("Loading…") : files.length ? t("Show older") : t("Look further back")}
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </Page>
  );
}
